<template>
    <div class="custom-select" @click="toggleDropdown" ref="select">
        <div class="selected-value" :class="{ error: error.hasError, open: isOpen }">
            <input v-if="isOpen&&isSearchable" v-model="searchText" ref="searchInput" class="search-input" placeholder="Search..." name="search" @click.stop @keyup.stop />
            <span v-else>
                {{ internalValue ? internalValue.label : placeholder }}
            </span>
            <div class="icon-btn-container">
                <span v-if="isClearable&&internalValue" class="clear-btn" @click.stop="clearValue">
                    <i class="mdi mdi-close"></i>
                </span>
                <span class="arrow" :class="{ open: isOpen }">
                    <i class="mdi mdi-chevron-down"></i>
                </span>
            </div>
        </div>
        <p v-if="error.hasError&&!isOpen&&error.errorMessage" class="error-message">{{ error.errorMessage }}</p>
        <ul v-if="isOpen" class="options-list" ref="optionsList">
            <li v-for="(option, index) in filteredOptions" :key="option.value+index" :class="{ selected: isSelected(option), focused: focusedIndex === index }" @click.stop="selectOption(option)" @mouseenter="focusedIndex = index">
                {{ option.label }}
            </li>
            <li v-if="filteredOptions&&filteredOptions.length === 0" class="no-options">No options available</li>
        </ul>
    </div>
</template>

<script>
export default {
  name: 'CustomSelect',

  props: {
    value: {
      type: Object,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    onChange: {
      type: Function,
      default: null
    },
    isClearable: {
      type: Boolean,
      default: true
    },
    isSearchable: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: () => ({
        hasError: false,
        errorMessage: ''
      })
    }
  },

  data () {
    return {
      isOpen: false,
      internalValue: this.value,
      searchText: '',
      filteredOptions: [...this.options],
      focusedIndex: -1
    }
  },

  watch: {
    value (newVal) {
      this.internalValue = newVal
    },

    searchText () {
      this.filterOptions()
    },

    options: {
      immediate: true,
      handler (newOptions) {
        this.filteredOptions = [...newOptions]
      }
    },

    isOpen (newValue) {
      this.searchText = ''

      if (newValue && this.isSearchable) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    }
  },

  mounted () {
    document.addEventListener('click', this.onClickOutside, true)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onClickOutside, true)
  },

  methods: {
    toggleDropdown () {
      this.isOpen = !this.isOpen
    },

    selectOption (option) {
      this.internalValue = option
      this.hasError = false
      this.isOpen = false
      if (this.onChange) {
        this.onChange(this.internalValue, option)
      }
      this.$emit('input', option)
      this.$emit('change', option)
    },

    clearValue () {
      this.internalValue = null
      this.isOpen = false
      if (this.onChange) {
        this.onChange(this.internalValue, null)
      }
      this.$emit('input', null)
      this.$emit('change', null)
    },

    isSelected (option) {
      return this.internalValue ? this.internalValue.value === option.value : false
    },

    onClickOutside (e) {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false
      }
    },

    filterOptions () {
      if (!this.isSearchable || !this.searchText.trim()) {
        this.filteredOptions = [...this.options]
        return
      }

      const query = this.searchText.toLowerCase()

      this.filteredOptions = this.options.filter(option => {
        const label = option.label || ''
        return label.toLowerCase().includes(query)
      })
    }
  }
}
</script>

<style scoped>
.custom-select {
  position: relative;
  width: 100%;
  user-select: none;
  outline: none;
  cursor: pointer;
  font-family: Arial, sans-serif;
}

.selected-value {
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: white;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-value.open {
  border-color: rgb(var(--primary-color));
}

.arrow {
  margin-left: 10px;
  transition: transform 0.2s ease;
  font-size: 1.2em;
  color: #666;
}

.arrow.open {
  transform: rotate(180deg);
}

.error .arrow {
  color: rgb(var(--error-color));
}
.options-list {
  position: absolute;
  z-index: 99;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-top: 2px;
  background: white;
  padding: 0;
  list-style: none;
}

.options-list li {
  padding: 8px 12px;
  min-height: 40px;
  font-size: 14px;
  cursor: pointer;
}

.options-list li.selected {
  background-color: rgba(var(--primary-color), 0.2);
  color: rgb(var(--primary-color));
}

.options-list li.selected:hover {
  background-color: rgba(var(--primary-color),0.2);;
  color: rgb(var(--primary-color));
}

.options-list li:hover {
  background-color: rgba(var(--primary-color), 0.2);
}

.options-list li.no-options {
  color: #999;
  cursor: default;
  text-align: center;
}

.selected-value.error{
  background-color: rgb(var(--error-background-color)) !important;
  color: black;
  border-color: rgb(var(--error-color)) !important
}

.clear-btn:hover {
  color: rgb(var(--error-color));
}

.icon-btn-container {
  display: flex;
  align-items: center;
}

.error-message {
  padding: 4px;
  margin-top: 2px;
  border-radius: 12px;
  width: 100%;
  text-align: center;
  font-size: 0.75rem;
  color: rgb(var(--error-color));
  background: white;
}

.search-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 14px;
  padding: 4px 0;
}
</style>
